/**
 * Helper functions to process specific keys.
 */

import { CLINIC_REPORT_STATES } from "static/constants";
import { convertRpmDeviceObject } from "./rpmDevice";

export const processPatientInformation = (info, state) => {
  const {
    diagnosis,
    strokeHistory,
    strokeHistoryStatus,
    anticoag_information: anticoagInfo,
    physicians,
    referring_provider: referringProvider,
    diagnosisHistory,
    preferred_language: preferredLanguage,
    ...restInfo
  } = info;

  const {
    anticoag,
    anticoag_status: anticoagStatus,
    refuses_oac: refusesOAC,
    updated_at: anticoagUpdatedAt,
    updated_by: anticoagUpdatedBy,
  } = anticoagInfo || {};

  state.originalInformation = restInfo;
  state.information = restInfo;

  const historyValues = {
    strokeHistory,
    strokeHistoryStatus,
    anticoag,
    refusesOAC,
    anticoagStatus,
    anticoagUpdatedAt,
    anticoagUpdatedBy: anticoagUpdatedBy?.name,
    diagnosisHistory: Array.isArray(diagnosisHistory)
      ? [...diagnosisHistory].sort(
          (a, b) => new Date(b?.diagnosedAt) - new Date(a?.diagnosedAt)
        )
      : [],
    patientDiagnosis: diagnosis || [],
  };

  state.patientHistoryInformation = historyValues;
  state.patientOriginalHistoryInformation = historyValues;

  state.profile.physicians = physicians;
  state.profile.idtf = restInfo?.idtf ?? null;
  state.profile.preferredLanguage = preferredLanguage || "";
  state.profile.laao_device = restInfo?.laao_device ?? null;
  state.profile.referringProviderId = referringProvider?.provider_id || "";
  state.profile.need_magnet_during_procedures =
    restInfo?.need_magnet_during_procedures ?? null;
  state.profile.alertWhenDisconnected =
    restInfo?.alert_when_disconnected || false;
  state.profile.monthlyBatteryCheck =
    restInfo?.monthly_battery_check?.status || false;
  state.profile.alertsOnly = restInfo?.alerts_only ?? null;
  state.profile.inOfficeOnly = restInfo?.in_office_only || false;
  state.profile.statusLetter = restInfo?.status_letter || false;

  state.originalProfile.physicians = physicians;
  state.originalProfile.idtf = restInfo?.idtf ?? null;
  state.originalProfile.laao_device = restInfo?.laao_device ?? null;
  state.originalProfile.referringProviderId =
    referringProvider?.provider_id || "";
  state.originalProfile.need_magnet_during_procedures =
    restInfo?.need_magnet_during_procedures ?? null;
  state.originalProfile.alertsOnly = restInfo?.alerts_only || false;
  state.originalProfile.monthlyBatteryCheck =
    restInfo?.monthly_battery_check?.status || false;
  state.originalProfile.inOfficeOnly = restInfo?.in_office_only || false;
  state.originalProfile.statusLetter = restInfo?.status_letter || false;
};

export const processDeviceInformation = ({
  deviceInfo,
  key,
  originalKey,
  state,
}) => {
  const { mode, updated_at, ra, rv, lv, ...filteredInfo } = {
    ...state[originalKey],
    ...deviceInfo,
  };

  state[key] = filteredInfo;
  state[originalKey] = filteredInfo;
};

export const processSecondaryDeviceInformation = (deviceInfo, state) => {
  const { mode, updated_at, ra, rv, lv, ...filteredInfo } = {
    ...state.originalSecondaryDeviceInformation,
    ...deviceInfo,
  };

  state.secondaryDeviceInformation = {
    ...filteredInfo,
    ...(filteredInfo?.rpm_devices && {
      rpm_devices: convertRpmDeviceObject(filteredInfo.rpm_devices, state),
    }),
  };

  state.originalSecondaryDeviceInformation = filteredInfo;
};

export const processInitialReportTab = (value, state) => {
  const hfToClinic = CLINIC_REPORT_STATES.includes(value?.hf);
  state.profile.hfToClinic = hfToClinic;
  state.originalProfile.hfToClinic = hfToClinic;
};

export const processBillingSchedule = (billingSchedule, state) => {
  const { day_31, day_91, day_custom } = billingSchedule || {};

  state.originalBillingSchedule = {
    day31: day_31?.active ?? false,
    day31BillingCode: day_31?.billing_code ?? "",
    day91: day_91?.active ?? false,
    day91BillingCode: day_91?.billing_code ?? "",
    custom: {
      days: day_custom?.days ?? 0,
      enabled: day_custom?.active ?? false,
      customBillingCode: day_custom?.billing_code ?? "",
    },
  };

  state.billingSchedule = { ...state.originalBillingSchedule };
};

export const updateDeviceInformationWithRPM = (rpmDevices, state) => {
  const rpmDevicesInfo = convertRpmDeviceObject(rpmDevices, state);
  state.deviceInformation = {
    ...state.deviceInformation,
    rpm_devices: rpmDevicesInfo,
  };
  state.originalDeviceInformation = {
    ...state.originalDeviceInformation,
    rpm_devices: rpmDevicesInfo,
  };
};

export const updateProfileValue = ({ key, value, state }) => {
  if (key) {
    state.profile[key] = value;
    state.originalProfile[key] = value;
  }
};
